import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  KeyboardAvoidingView,
  ScrollView,
  StyleSheet,
  View,
  Text,
  TouchableOpacity,
  Platform,
  type LayoutChangeEvent,
} from "react-native";
import { useTranslation } from "react-i18next";
import { ComponentStyles } from "../../../../constants/componentStyles";
import { useSessionStore } from "../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../state/src/useSessionStore";
import { useApiWithLoading } from "../../../../hooks/useApiWithLoading";
import { useGlobalError } from "../../../uiComponents/GlobalErrorProvider";
import {
  mapiQuote,
  mapiTransactionFieldsSend,
  mapiCreateProfile,
  mapiUpdateTransaction,
  mapiGetCountryDetails,
} from "../../../../services/mapi.service";
import { primeMapiSubdivisionsCache } from "../../../../helper/mapiSubdivisionsCache";

import BackIcon from "../../../../../../../assets/images/BackIcon.svg";
import CloseIcon from "../../../../../../../assets/images/CloseIcon.svg";
import DownArrow from "../../../../../../../assets/images/DownArrowIcon.svg";
import WarninBlackIcon from "../../../../../../../assets/images/WarningBlackIcon.svg";
import Colors from "../../../../constants/colors";
import GradientBackground from "../../../uiComponents/GradientBackground";

import { ReactCountryFlag } from "react-country-flag";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import { capturePosthogEvent } from "../../../../utils/analytics";
import type { WebFlowRoutingProps } from "../../../../navigation/webFlowRouting";
import { useWebFlowStepSync } from "../../../../hooks/useWebFlowStepSync";

import MapiEnterPhoneStep, {
  MapiEnterPhoneStepHandle,
} from "./steps/MapiEnterPhoneStep";
import MapiPinInputStep from "./steps/MapiPinInputStep";
import MapiChooseCountryStep from "./steps/MapiChooseCountryStep";
import MapiChooseStateStep from "./steps/MapiChooseStateStep";
import MapiSendAmountEntryStep from "./steps/MapiSendAmountEntryStep";
import MapiHowTheyReceiveStep from "./steps/MapiHowTheyReceiveStep";
import MapiReviewAndEditStep from "./steps/MapiReviewAndEditStep";
import MapiEnterSenderNameStep, {
  MapiEnterSenderNameStepHandle,
} from "./steps/MapiEnterSenderNameStep";
import MapiSenderAddressStep, {
  MapiSenderAddressStepHandle,
} from "./steps/MapiSenderAddressStep";
import MapiSenderDateOfBirthStep, {
  MapiSenderDateOfBirthStepHandle,
} from "./steps/MapiSenderDateOfBirthStep";
import MapiSenderIDStep, {
  MapiSenderIDStepHandle,
} from "./steps/MapiSenderIDStep";
import MapiSenderID2Step, {
  MapiSenderID2StepHandle,
} from "./steps/MapiSenderID2Step";
import MapiReceiverDetailsStep, {
  MapiReceiverDetailsStepHandle,
} from "./steps/MapiReceiverDetailsStep";
import MapiFraudPreventionStep from "./steps/MapiFraudPreventionStep";
import MapiReviewFinalStep from "./steps/MapiReviewFinalStep";
import MapiHeadToCashierStep from "./steps/MapiHeadToCashierStep";
import {
  quoteFeeRequestDefaults,
  mapiSenderAddressFieldDisplayOrder,
  mapiSenderAddressFields,
} from "../../../../constants/send.constant";
import { normalizeUsCountrySubdivisionCode } from "../../../../helper/mapiSubdivisionCode";
import {
  buildMapiQuoteParamsFromSessionState,
  findMatchingMapiTransaction,
  formatQuoteDataErrors,
  getFxFromQuoteData,
  getMapiQuoteTransactions,
  mapiPromotionInfoHasError,
} from "../../../../helper/mapiQuoteResponseHelpers";

countries.registerLocale(enLocale);

type StepDef = {
  key: string;
};

const MAPI_STEPS_WITH_PIN: StepDef[] = [
  { key: "phone" },
  { key: "pin" },
  { key: "country" },
  { key: "state" },
  { key: "amount" },
  { key: "howTheyReceive" },
  { key: "review" },
  { key: "senderName" },
  { key: "senderAddress" },
  { key: "senderDOB" },
  { key: "senderID" },
  { key: "receiverDetails" },
  { key: "fraudPrevention" },
  { key: "reviewFinal" },
  { key: "headToCashier" },
];

const MAPI_STEPS_WITHOUT_PIN: StepDef[] = [
  { key: "phone" },
  { key: "country" },
  { key: "state" },
  { key: "amount" },
  { key: "howTheyReceive" },
  { key: "review" },
  { key: "senderName" },
  { key: "senderAddress" },
  { key: "senderDOB" },
  { key: "senderID" },
  { key: "receiverDetails" },
  { key: "fraudPrevention" },
  { key: "reviewFinal" },
  { key: "headToCashier" },
];

interface MapiMainSendScreenProps {
  handleClose?: () => void;
  handleFinish?: () => void;
  /** Web: sync URL `/sendMapi/:stepSlug` with active step (optional; native omits). */
  webFlowRouting?: WebFlowRoutingProps;
}

export const MapiMainSendCashScreen: React.FC<MapiMainSendScreenProps> = ({
  handleClose,
  handleFinish,
  webFlowRouting,
}) => {
  const { t } = useTranslation(["translation", "delivery"]);
  const { executeWithLoading } = useApiWithLoading();
  const { showError } = useGlobalError();

  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const selectedDestinationCountryCode = useSessionStore(
    (s: SessionState) => s.selectedDestinationCountryCode,
  );
  const selectedDestinationCountryName = useSessionStore(
    (s: SessionState) => s.selectedDestinationCountryName,
  );
  const selectedDestinationStateAbbr = useSessionStore(
    (s: SessionState) => s.selectedDestinationStateAbbr,
  );
  const setSelectedReceiveCurrencyGlobal = useSessionStore(
    (s: SessionState) => s.setSelectedReceiveCurrency,
  );
  const setMapiSenderPrimaryIdentification = useSessionStore(
    (s: SessionState) => (s as any).setMapiSenderPrimaryIdentification,
  );
  const setMapiSenderSecondaryIdentification = useSessionStore(
    (s: SessionState) => (s as any).setMapiSenderSecondaryIdentification,
  );
  const setMapiReceiverDetails = useSessionStore(
    (s: SessionState) => (s as any).setMapiReceiverDetails,
  );
  const setConfirmationNumber = useSessionStore(
    (s: SessionState) => (s as any).setConfirmationNumber,
  );
  const setMapiSubdivisionsList = useSessionStore(
    (s: SessionState) => (s as any).setMapiSubdivisionsList,
  );
  const setMapiTransactionFieldsData = useSessionStore(
    (s: SessionState) => (s as any).setMapiTransactionFieldsData,
  );
  const setMapiQuoteResponseStore = useSessionStore(
    (s: SessionState) => (s as any).setMapiQuoteResponse,
  );
  const setPendingSendOptionStore = useSessionStore(
    (s: SessionState) => (s as any).setPendingSendOption,
  );
  const setSelectedSendOptionStore = useSessionStore(
    (s: SessionState) => (s as any).setSelectedSendOption,
  );
  const setSelectedDeliveryOptionStore = useSessionStore(
    (s: SessionState) => (s as any).setSelectedDeliveryOptionStore,
  );
  const setIsPromocodeAppliedStore = useSessionStore(
    (s: SessionState) => (s as any).setIsPromocodeApplied,
  );
  const setIsPromoActivityIndicatorStore = useSessionStore(
    (s: SessionState) => (s as any).setIsPromoActivityIndicator,
  );
  const isPromocodeApplied = useSessionStore(
    (s: SessionState) => (s as any).isPromocodeApplied,
  );

  const [steps, setSteps] = useState<StepDef[]>(MAPI_STEPS_WITH_PIN);
  const [currentStep, setCurrentStep] = useState(0);
  const [isStepValid, setIsStepValid] = useState(false);
  const [pinResetSignal, setPinResetSignal] = useState(0);
  const [exchangeRate, setExchangeRate] = useState<number>(1);
  const [receiveCurrency, setReceiveCurrency] = useState<string>("");
  const [isGradientExpand, setIsGradientExpand] = useState(false);
  const [footerHeight, setFooterHeight] = useState(0);

  const pendingSendOption = useSessionStore(
    (s: SessionState) => (s as any).pendingSendOption,
  );
  const [isQuoteLoading, setIsQuoteLoading] = useState(false);
  const [mapiConfirmationCode, setMapiConfirmationCode] = useState<string>("");
  const mapiPromoCode = useSessionStore(
    (s: SessionState) => (s as any).mapiPromoCode ?? "",
  );
  const setMapiPromoCodeStore = useSessionStore(
    (s: SessionState) => (s as any).setMapiPromoCode,
  );
  const [mapiPromoError, setMapiPromoError] = useState("");

  const phoneStepRef = useRef<MapiEnterPhoneStepHandle | null>(null);
  const senderNameStepRef = useRef<MapiEnterSenderNameStepHandle | null>(null);
  const senderAddressStepRef = useRef<MapiSenderAddressStepHandle | null>(null);
  const senderDOBStepRef = useRef<MapiSenderDateOfBirthStepHandle | null>(null);
  const senderIDStepRef = useRef<MapiSenderIDStepHandle | null>(null);
  const senderID2StepRef = useRef<MapiSenderID2StepHandle | null>(null);
  const receiverDetailsStepRef = useRef<MapiReceiverDetailsStepHandle | null>(
    null,
  );
  const scrollRef = useRef<ScrollView>(null);

  const currentKey = steps[currentStep]?.key;

  const alpha2 = useMemo(() => {
    const a3 = (selectedDestinationCountryCode || "").toUpperCase();
    return countries.alpha3ToAlpha2(a3) || "";
  }, [selectedDestinationCountryCode]);

  useEffect(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ y: 0, animated: false });
    });
  }, [currentStep]);

  useWebFlowStepSync(webFlowRouting, steps, currentStep, setCurrentStep);

  const extractExchangeRateFromQuote = (data: any) => {
    const { rate, receiveCurrency } = getFxFromQuoteData(data);
    setExchangeRate(rate);
    setReceiveCurrency(receiveCurrency);
    try {
      setSelectedReceiveCurrencyGlobal(receiveCurrency || null);
    } catch {}
  };

  const persistMapiQuoteAfterSuccess = (data: any, previousPending: any) => {
    try {
      setMapiQuoteResponseStore?.(data);
    } catch {
      /* ignore */
    }
    extractExchangeRateFromQuote(data);
    const txs = getMapiQuoteTransactions(data);
    if (!Array.isArray(txs) || txs.length === 0) return;
    if (previousPending == null) return;
    const match = findMatchingMapiTransaction(txs, previousPending);
    if (!match) return;
    try {
      setPendingSendOptionStore?.(match);
    } catch {
      /* ignore */
    }
    try {
      setSelectedSendOptionStore?.(match);
    } catch {
      /* ignore */
    }
    try {
      setSelectedDeliveryOptionStore?.({
        DeliveryOption:
          match?.serviceOptionCode ?? match?.DeliveryOption ?? null,
        option: match,
      });
    } catch {
      /* ignore */
    }
  };

  const callInitialQuote = async (overrideCountry?: string) => {
    const storeState = useSessionStore.getState();
    const destCountry =
      overrideCountry || storeState.selectedDestinationCountryCode;
    const agent = storeState.agentId;
    if (!agent || !destCountry) return;

    const amountIdx = steps.findIndex((s) => s.key === "amount");
    if (amountIdx !== -1) setCurrentStep(amountIdx);

    // put the defalt values in some config file.
    await executeWithLoading(async () => {
      try {
        setIsQuoteLoading(true);
        const quoteParams: any = {
          agentPartnerId: agent,
          destinationCountryCode: destCountry,
          serviceOptionCode: quoteFeeRequestDefaults.serviceOptionCode, // default to will call as we refer it for fx rate calculation. : Sara to confirm whats the default value: based on country??
          sendAmount: {
            value: quoteFeeRequestDefaults.defaultSendAmount,
            currencyCode: quoteFeeRequestDefaults.currencyCode, // agentCountry currencty
          },
        };

        const subdiv = storeState.selectedDestinationStateAbbr; //buildSubdivisionCode(destCountry);
        if (subdiv) {
          quoteParams.destinationCountrySubdivisionCode = subdiv;
        }
        quoteParams.fundInStore = {
          fundInStore: quoteFeeRequestDefaults.fundInStore,
          fundInStoreAgentPartnerId: storeState.agentId,
        };
        quoteParams.sendAmountIncludingFee =
          quoteFeeRequestDefaults.sendAmountIncludingFee;

        const response = await mapiQuote(quoteParams);

        if (response.success && response.data) {
          try {
            setMapiQuoteResponseStore?.(response.data);
          } catch {
            /* ignore */
          }
          extractExchangeRateFromQuote(response.data);
        }
      } catch (e: any) {
        console.error("Initial MAPI quote failed:", e);
      } finally {
        setIsQuoteLoading(false);
      }
    }, "Fetching rates...");
  };

  const handleAmountContinue = async () => {
    const storeState = useSessionStore.getState() as any;
    const sendAmt = storeState.currentSendAmount;
    const amount = parseFloat((sendAmt || "0").replace(/,/g, ""));
    if (!isFinite(amount) || amount <= 0) {
      showError("Please enter a valid amount.");
      return;
    }

    const agent = storeState.agentId;
    const destCountry = storeState.selectedDestinationCountryCode;
    if (!agent || !destCountry) return;

    await executeWithLoading(async () => {
      try {
        // add receiveCurrencyCode to quoteParams based on the selected receive currency
        const receiveCurrencyCode = storeState.selectedReceiveCurrency;
        const quoteParams: any = {
          agentPartnerId: agent,
          destinationCountryCode: destCountry,
          sendAmount: { value: amount, currencyCode: "USD" },
        };
        if (receiveCurrencyCode) {
          quoteParams.receiveCurrencyCode = receiveCurrencyCode;
        }

        const subdiv = storeState.selectedDestinationStateAbbr;
        if (subdiv) {
          quoteParams.destinationCountrySubdivisionCode = subdiv;
        }
        quoteParams.fundInStore = {
          fundInStore: quoteFeeRequestDefaults.fundInStore,
          fundInStoreAgentPartnerId: storeState.agentId,
        };
        quoteParams.sendAmountIncludingFee =
          quoteFeeRequestDefaults.sendAmountIncludingFee;

        const s2 = useSessionStore.getState() as any;
        const promoTrim = (s2.mapiPromoCode || "").trim();
        if (s2.isPromocodeApplied && promoTrim) {
          quoteParams.promotionCodes = [promoTrim];
        }

        const response = await mapiQuote(quoteParams);

        if (response.success && response.data) {
          const prev = (useSessionStore.getState() as any).pendingSendOption;
          if (prev) {
            persistMapiQuoteAfterSuccess(response.data, prev);
          } else {
            try {
              setMapiQuoteResponseStore?.(response.data);
            } catch {
              /* ignore */
            }
            extractExchangeRateFromQuote(response.data);
          }
        }

        const howIdx = steps.findIndex((s) => s.key === "howTheyReceive");
        if (howIdx !== -1) setCurrentStep(howIdx);
      } catch (e: any) {
        showError(e?.message || "Quote failed.");
      }
    }, "Calculating options...");

    capturePosthogEvent("Mapi Send Flow - Amount Capture Step", {
      "data-mg-sending-amount": sendAmt,
    });
  };

  const handleValidationChange = (valid: boolean) => {
    setIsStepValid(valid);
  };

  const handleCurrencyChange = (currency: string, rate: number) => {
    setExchangeRate(rate);
    setReceiveCurrency(currency);
    try {
      setSelectedReceiveCurrencyGlobal(currency || null);
    } catch {}
  };

  const updateMapiPromocode = (text: string) => {
    setMapiPromoCodeStore?.(text);
    setMapiPromoError("");
  };

  const handleApplyMapiPromoCode = async () => {
    const trimmed = mapiPromoCode.trim();
    if (!trimmed) {
      setMapiPromoError("Please enter a promo code");
      return;
    }
    setMapiPromoCodeStore?.(trimmed);
    setMapiPromoError("");
    setIsPromoActivityIndicatorStore?.(true);
    const previousPending = (useSessionStore.getState() as any)
      .pendingSendOption;
    try {
      const quoteParams = buildMapiQuoteParamsFromSessionState(
        useSessionStore.getState() as any,
        [trimmed],
      );
      if (!quoteParams) {
        showError("Unable to apply promo code.");
        setIsPromocodeAppliedStore?.(false);
        return;
      }
      const response = await mapiQuote(quoteParams as any);
      if (!response?.success || !response.data) {
        setMapiPromoError(t("PROMOCODE_ERROR_OCCURRED"));
        setIsPromocodeAppliedStore?.(false);
        return;
      }
      const data = response.data;
      const payloadErr = formatQuoteDataErrors(data);
      if (payloadErr) {
        showError(payloadErr);
        setMapiPromoError(t("PROMOCODE_ERROR_OCCURRED"));
        setIsPromocodeAppliedStore?.(false);
        return;
      }
      const txs = getMapiQuoteTransactions(data);
      if (!Array.isArray(txs) || txs.length === 0) {
        setMapiPromoError(t("PROMOCODE_ERROR_OCCURRED"));
        setIsPromocodeAppliedStore?.(false);
        return;
      }
      const match = findMatchingMapiTransaction(txs, previousPending);
      if (mapiPromotionInfoHasError(match?.promotionInfo)) {
        setMapiPromoError(t("PROMOCODE_ERROR_OCCURRED"));
        setIsPromocodeAppliedStore?.(false);
        return;
      }
      persistMapiQuoteAfterSuccess(data, previousPending);
      setIsPromocodeAppliedStore?.(true);
      setMapiPromoError("");
    } catch (e: any) {
      setMapiPromoError(t("PROMOCODE_ERROR_OCCURRED"));
      setIsPromocodeAppliedStore?.(false);
      try {
        showError(e?.message || t("PROMOCODE_ERROR_OCCURRED"));
      } catch {
        /* ignore */
      }
    } finally {
      setIsPromoActivityIndicatorStore?.(false);
    }
  };

  const clearMapiPromoCode = async () => {
    if (!isPromocodeApplied) {
      setMapiPromoCodeStore?.("");
      setMapiPromoError("");
      return;
    }
    setIsPromoActivityIndicatorStore?.(true);
    const previousPending = (useSessionStore.getState() as any)
      .pendingSendOption;
    try {
      const quoteParams = buildMapiQuoteParamsFromSessionState(
        useSessionStore.getState() as any,
        undefined,
      );
      if (!quoteParams) {
        showError(t("PROMOCODE_ERROR_OCCURRED"));
        return;
      }
      const response = await mapiQuote(quoteParams as any);
      if (!response?.success || !response.data) {
        showError(t("PROMOCODE_ERROR_OCCURRED"));
        return;
      }
      const data = response.data;
      const payloadErr = formatQuoteDataErrors(data);
      if (payloadErr) {
        showError(payloadErr);
        return;
      }
      const txs = getMapiQuoteTransactions(data);
      if (!Array.isArray(txs) || txs.length === 0) {
        showError(t("PROMOCODE_ERROR_OCCURRED"));
        return;
      }
      persistMapiQuoteAfterSuccess(data, previousPending);
      setMapiPromoCodeStore?.("");
      setIsPromocodeAppliedStore?.(false);
    } catch (e: any) {
      try {
        showError(e?.message || "Quote failed.");
      } catch {
        /* ignore */
      }
    } finally {
      setIsPromoActivityIndicatorStore?.(false);
    }
  };

  const handleNext = async (countryCode?: string) => {
    await executeWithLoading(async () => {
      if (currentKey === "phone") {
        try {
          await phoneStepRef.current?.submit();
        } catch {
          return;
        }
      } else if (currentKey === "country") {
        if (countryCode) {
          try {
            const details = await mapiGetCountryDetails(countryCode);
            const subdivisions =
              details.success && details.data
                ? details.data[0]?.subdivisions
                : [];
            const subdivList = Array.isArray(subdivisions) ? subdivisions : [];
            setMapiSubdivisionsList?.(subdivList);
            primeMapiSubdivisionsCache(countryCode, subdivList);
            if (Array.isArray(subdivisions) && subdivisions.length > 0) {
              const stateIdx = steps.findIndex((s) => s.key === "state");
              if (stateIdx !== -1) setCurrentStep(stateIdx);
            } else {
              callInitialQuote(countryCode);
            }
          } catch {
            setMapiSubdivisionsList?.([]);
            callInitialQuote(countryCode);
          }
        }
      } else if (currentKey === "state") {
        callInitialQuote();
      } else if (currentKey === "amount") {
        await handleAmountContinue();
      } else if (currentKey === "howTheyReceive") {
        // Same as send flow: rely on store pending option (auto-advance can run with stale isStepValid).
        const pendingFromStore = (useSessionStore.getState() as any)
          .pendingSendOption;
        if (!pendingFromStore) {
          showError("Please select delivery option");
          return;
        }
        (useSessionStore.getState() as any).setSelectedSendOption?.(
          pendingFromStore,
        );
        const reviewIdx = steps.findIndex((s) => s.key === "review");
        if (reviewIdx !== -1) setCurrentStep(reviewIdx);
      } else if (currentKey === "senderName") {
        const ok = await senderNameStepRef.current?.submit();
        if (!ok) return;
        const addrIdx = steps.findIndex((s) => s.key === "senderAddress");
        if (addrIdx !== -1) setCurrentStep(addrIdx);
      } else if (currentKey === "senderAddress") {
        const ok = await senderAddressStepRef.current?.submit();
        if (!ok) return;
        const dobIdx = steps.findIndex((s) => s.key === "senderDOB");
        if (dobIdx !== -1) setCurrentStep(dobIdx);
      } else if (currentKey === "senderDOB") {
        const ok = await senderDOBStepRef.current?.submit();
        if (!ok) return;
        const senderIDIdx = steps.findIndex((s) => s.key === "senderID");
        if (senderIDIdx !== -1) setCurrentStep(senderIDIdx);
        else handleFinish?.();
      } else if (currentKey === "senderID") {
        const ok = await senderIDStepRef.current?.submit();
        if (!ok) return;
        const primaryValues = senderIDStepRef.current?.getValues?.() ?? {};
        setMapiSenderPrimaryIdentification?.(primaryValues);
        const senderID2Idx = steps.findIndex((s) => s.key === "senderID2");
        if (senderID2Idx !== -1) setCurrentStep(senderID2Idx);
        else {
          const receiverIdx = steps.findIndex(
            (s) => s.key === "receiverDetails",
          );
          if (receiverIdx !== -1) setCurrentStep(receiverIdx);
          else handleFinish?.();
        }
      } else if (currentKey === "senderID2") {
        const ok = await senderID2StepRef.current?.submit();
        if (!ok) return;
        const secondaryValues = senderID2StepRef.current?.getValues?.() ?? {};
        setMapiSenderSecondaryIdentification?.(secondaryValues);
        const receiverIdx = steps.findIndex((s) => s.key === "receiverDetails");
        if (receiverIdx !== -1) setCurrentStep(receiverIdx);
        else handleFinish?.();
      } else if (currentKey === "receiverDetails") {
        const ok = await receiverDetailsStepRef.current?.submit();
        if (!ok) return;
        const receiverValues =
          receiverDetailsStepRef.current?.getValues?.() ?? {};
        setMapiReceiverDetails?.(receiverValues);
        const fraudIdx = steps.findIndex((s) => s.key === "fraudPrevention");
        if (fraudIdx !== -1) setCurrentStep(fraudIdx);
        else handleFinish?.();
      } else if (currentKey === "fraudPrevention") {
        const reviewFinalIdx = steps.findIndex((s) => s.key === "reviewFinal");
        if (reviewFinalIdx !== -1) setCurrentStep(reviewFinalIdx);
        else handleFinish?.();
      }
    }, "Processing...");
  };

  const dotKeysToNested = (flatObj: Record<string, string>): any => {
    const result: any = {};
    for (const [dotKey, value] of Object.entries(flatObj)) {
      const parts = dotKey.split(".");
      let current = result;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
    }
    return result;
  };

  const buildFieldValueMap = (storeState: any): Record<string, string> => {
    const dobString = storeState.senderDOBString || "";
    let isoDateOfBirth = "";
    if (dobString) {
      const parts = dobString.split("/");
      if (parts.length === 3) {
        isoDateOfBirth = `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}T00:00:00.000Z`;
      }
    }

    const map: Record<string, string> = {};

    map["sender.name.firstName"] = storeState.senderFirstName || "";
    map["sender.name.middleName"] = storeState.senderMiddleName || "";
    map["sender.name.lastName"] = storeState.senderLastName || "";
    const mapiAddr = storeState.mapiSenderAddressValues || {};
    for (const tag of mapiSenderAddressFieldDisplayOrder) {
      const v = mapiAddr[tag];
      if (typeof v === "string" && v.trim()) {
        const trimmed = v.trim();
        map[tag] =
          tag === mapiSenderAddressFields.senderState
            ? normalizeUsCountrySubdivisionCode(trimmed)
            : trimmed;
      }
    }
    map["sender.mobilePhone.number"] = (storeState.phoneNumber || "").replace(
      /\D/g,
      "",
    );
    map["sender.mobilePhone.countryDialCode"] = "1";
    map["sender.personalDetails.dateOfBirth"] = isoDateOfBirth;
    map["sender.personalDetails.birthCountryCode"] = "USA";

    const mergeStore = (store: Record<string, string> | null | undefined) => {
      if (!store || typeof store !== "object") return;
      for (const [key, val] of Object.entries(store)) {
        if (val) map[key] = val as string;
      }
    };

    mergeStore(storeState.mapiSenderPrimaryIdentification);
    mergeStore(storeState.mapiSenderSecondaryIdentification);
    mergeStore(storeState.mapiReceiverDetails);

    return map;
  };

  const handleUpdateTransaction = async () => {
    const storeState = useSessionStore.getState() as any;
    const selectedOption = storeState.pendingSendOption;
    const transactionId = selectedOption?.transactionId;

    if (!transactionId) {
      showError("Transaction ID not found. Please try again.");
      return;
    }

    const destCountry = storeState.selectedDestinationCountryCode || "";
    const serviceOption = selectedOption?.serviceOptionCode || "WILL_CALL";
    const sendAmountValue = Number(
      selectedOption?.sendAmount?.amount?.value ?? 0,
    );
    const sendCurrencyCode =
      selectedOption?.sendAmount?.amount?.currencyCode || "USD";
    const recvCurrencyCode =
      selectedOption?.receiveAmount?.amount?.currencyCode || "USD";

    const fieldInfoList: any[] =
      storeState.mapiTransactionFieldsData?.fieldInfo ?? [];
    const valueMap = buildFieldValueMap(storeState);

    const collectedFields: Record<string, string> = {};
    for (const fi of fieldInfoList) {
      const key: string = fi?.field ?? "";
      if (!key) continue;
      const value = valueMap[key];
      if (value !== undefined && value !== "") {
        collectedFields[key] = value;
      }
    }

    const nestedFields = dotKeysToNested(collectedFields);

    const destStateAbbr = storeState.selectedDestinationStateAbbr || "";

    const updateData: any = {
      targetAudience: "CONSUMER_FACING",
      agentPartnerId: storeState.agentId,
      destinationCountryCode: destCountry,
      serviceOptionCode: serviceOption,
      sendAmount: {
        value: sendAmountValue,
        currencyCode: sendCurrencyCode,
      },
      receiveCurrencyCode: recvCurrencyCode,
      fundInStore: {
        fundInStore: quoteFeeRequestDefaults.fundInStore,
        fundInStoreAgentPartnerId: storeState.agentId,
      },
      ...(selectedOption?.serviceOptionRoutingCode && {
        serviceOptionRoutingCode: selectedOption?.serviceOptionRoutingCode,
      }),
      ...nestedFields,
    };

    if (destStateAbbr) {
      updateData.destinationCountrySubdivisionCode = destStateAbbr;
    }

    const dobString = storeState.senderDOBString || "";
    let dateOfBirth = "";
    if (dobString) {
      const parts = dobString.split("/");
      if (parts.length === 3) {
        dateOfBirth = `${parts[2]}-${parts[0].padStart(2, "0")}-${parts[1].padStart(2, "0")}`;
      }
    }

    const mapiAddrForProfile = storeState.mapiSenderAddressValues || {};

    const profileData: any = {
      targetAudience: "CONSUMER_FACING",
      agentPartnerId: String(storeState.agentId),
      userLanguage: "en-US",
      consumer: {
        address: {
          line1: mapiAddrForProfile["sender.address.line1"] || "",
          city: mapiAddrForProfile["sender.address.city"] || "",
          countrySubdivisionCode: normalizeUsCountrySubdivisionCode(
            mapiAddrForProfile["sender.address.countrySubdivisionCode"] || "",
          ),
          countryCode: mapiAddrForProfile["sender.address.countryCode"] || "",
          postalCode: mapiAddrForProfile["sender.address.postalCode"] || "",
        },
        mobilePhone: {
          number: (storeState.phoneNumber || "").replace(/\D/g, ""),
          countryDialCode: "1",
        },
        firstName: storeState.senderFirstName || "",
        lastName: storeState.senderLastName || "",
        dateOfBirth: dateOfBirth,
      },
    };

    await executeWithLoading(async () => {
      try {
        // Step 1: Create Profile
        capturePosthogEvent("Mapi Send Flow - Create Profile Start", {
          "data-mg-transaction-id": transactionId,
        });

        const profileResponse = await mapiCreateProfile({ profileData });

        if (!profileResponse.success) {
          showError(
            profileResponse.message ||
              profileResponse.error ||
              "Failed to create profile.",
          );
          return;
        }

        const profileId =
          profileResponse.data?.profileId || profileResponse.data?.id || "";
        capturePosthogEvent("Mapi Send Flow - Create Profile Success", {
          "data-mg-transaction-id": transactionId,
          "data-mg-profile-id": profileId,
        });

        // Step 2: Update Transaction (BFF auto-commits if readyForCommit)
        const response = await mapiUpdateTransaction({
          transactionId,
          updateData,
        });

        if (response.success && response.data) {
          const code =
            response.data.fundsInStoreConfirmationCode ||
            response.data.confirmationNumber ||
            response.data.referenceNumber ||
            "";
          capturePosthogEvent("Mapi Send Flow - Transaction Committed", {
            "data-mg-transaction-id": transactionId,
            "data-mg-confirmation-code": code,
          });
          setMapiConfirmationCode(code);
          setConfirmationNumber?.(code);
          const cashierIdx = steps.findIndex((s) => s.key === "headToCashier");
          if (cashierIdx !== -1) setCurrentStep(cashierIdx);
          else handleFinish?.();
        } else {
          showError(
            response.message ||
              response.error ||
              "Failed to update transaction.",
          );
        }
      } catch (e: any) {
        showError(e?.message || "An error occurred during submission.");
      }
    }, "Submitting transaction...");
  };

  const handleProfilesFound = (_profiles: any[]) => {
    const pinIdx = steps.findIndex((s) => s.key === "pin");
    if (pinIdx !== -1) setCurrentStep(pinIdx);
  };

  const handleNoProfilesFound = () => {
    setSteps(MAPI_STEPS_WITHOUT_PIN);
    const countryIdx = MAPI_STEPS_WITHOUT_PIN.findIndex(
      (s) => s.key === "country",
    );
    if (countryIdx !== -1) setCurrentStep(countryIdx);
  };

  const handlePinSuccess = () => {
    const countryIdx = steps.findIndex((s) => s.key === "country");
    if (countryIdx !== -1) setCurrentStep(countryIdx);
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleCountryEdit = () => {
    const countryIdx = steps.findIndex((s) => s.key === "country");
    if (countryIdx !== -1) setCurrentStep(countryIdx);
  };

  const handleEditStep = (key: string) => {
    const idx = steps.findIndex((s) => s.key === key);
    if (idx !== -1) setCurrentStep(idx);
  };

  useEffect(() => {
    if (
      currentKey === "phone" ||
      currentKey === "senderName" ||
      currentKey === "senderAddress" ||
      currentKey === "senderDOB" ||
      currentKey === "senderID" ||
      currentKey === "senderID2" ||
      currentKey === "receiverDetails"
    ) {
      setIsStepValid(false);
    } else {
      setIsStepValid(true);
    }
  }, [currentStep]);

  const showContinueButton =
    currentKey === "phone" ||
    currentKey === "amount" ||
    currentKey === "howTheyReceive" ||
    currentKey === "senderName" ||
    currentKey === "senderAddress" ||
    currentKey === "senderDOB" ||
    currentKey === "senderID" ||
    currentKey === "senderID2" ||
    currentKey === "receiverDetails" ||
    currentKey === "fraudPrevention";

  const showGradientFooter =
    currentKey === "review" || currentKey === "reviewFinal";

  const getMapiGradientFeeData = () => {
    const option: any = pendingSendOption;
    if (!option) {
      return {
        exchangeRate: 0,
        fees: 0,
        total: 0,
        sendCurrency: "USD",
        receiveCurrency: "—",
        totalSendTax: 0,
      };
    }

    const sendCurrency = option?.sendAmount?.amount?.currencyCode;
    const recvCurrency = option?.receiveAmount?.amount?.currencyCode;
    const fxRate = Number(option?.receiveAmount?.fxRate);
    const fees = Number(option?.sendAmount?.fees?.value);
    const total = Number(option?.sendAmount?.total?.value);

    const additionalCharges: any[] = option?.sendAmount?.additionalCharges;
    const fedTaxCharge = additionalCharges?.find(
      (c: any) => c?.typeCode === "mgiCountrySendFaceTax",
    );
    // mgiCountrySendFaceTax ==> U.S. Federal Remittance Tax
    // fedtax is applied for non us countries.
    const totalSendTax = Number(fedTaxCharge?.value) || 0;

    return {
      exchangeRate: fxRate,
      fees,
      total,
      sendCurrency,
      receiveCurrency: recvCurrency,
      totalSendTax,
    };
  };

  const renderCurrentStep = () => {
    switch (currentKey) {
      case "phone":
        return (
          <MapiEnterPhoneStep
            ref={phoneStepRef}
            testID="mapi-step-phone"
            onValidationChange={handleValidationChange}
            onProfilesFound={handleProfilesFound}
            onNoProfilesFound={handleNoProfilesFound}
          />
        );
      case "pin":
        return (
          <MapiPinInputStep
            testID="mapi-step-pin"
            resetSignal={pinResetSignal}
            onContinue={handlePinSuccess}
          />
        );
      case "country":
        return (
          <MapiChooseCountryStep
            testID="mapi-step-country"
            onCountrySelected={(code: string) => handleNext(code)}
          />
        );
      case "state":
        return (
          <MapiChooseStateStep
            testID="mapi-step-state"
            onCountrySelected={() => handleNext()}
          />
        );
      case "amount":
        return (
          <MapiSendAmountEntryStep
            testID="mapi-step-amount"
            exchangeRate={exchangeRate}
            receiveCurrency={receiveCurrency}
            isQuoteLoading={isQuoteLoading}
            onValidationChange={handleValidationChange}
            onCurrencyChange={handleCurrencyChange}
          />
        );
      case "howTheyReceive":
        return (
          <MapiHowTheyReceiveStep
            testID="mapi-step-howTheyReceive"
            exchangeRate={exchangeRate}
            receiveCurrency={receiveCurrency}
            onValidationChange={handleValidationChange}
            onCountrySelected={() => handleNext()}
          />
        );
      case "review":
        return (
          <MapiReviewAndEditStep
            testID="mapi-step-review"
            onEditStep={handleEditStep}
            promoCode={mapiPromoCode}
            errorText={mapiPromoError}
            updatePromocode={updateMapiPromocode}
            clearPromoCode={clearMapiPromoCode}
            handleApplyPromoCode={handleApplyMapiPromoCode}
          />
        );
      case "senderName":
        return (
          <MapiEnterSenderNameStep
            ref={senderNameStepRef}
            testID="mapi-step-senderName"
            onValidationChange={handleValidationChange}
          />
        );
      case "senderAddress":
        return (
          <MapiSenderAddressStep
            ref={senderAddressStepRef}
            testID="mapi-step-senderAddress"
            onValidationChange={handleValidationChange}
          />
        );
      case "senderDOB":
        return (
          <MapiSenderDateOfBirthStep
            ref={senderDOBStepRef}
            testID="mapi-step-senderDOB"
            onValidationChange={handleValidationChange}
          />
        );
      case "senderID":
        return (
          <MapiSenderIDStep
            ref={senderIDStepRef}
            testID="mapi-step-senderID"
            onValidationChange={handleValidationChange}
          />
        );
      case "senderID2":
        return (
          <MapiSenderID2Step
            ref={senderID2StepRef}
            testID="mapi-step-senderID2"
            onValidationChange={handleValidationChange}
          />
        );
      case "receiverDetails":
        return (
          <MapiReceiverDetailsStep
            ref={receiverDetailsStepRef}
            testID="mapi-step-receiverDetails"
            onValidationChange={handleValidationChange}
          />
        );
      case "fraudPrevention":
        return <MapiFraudPreventionStep />;
      case "reviewFinal":
        return (
          <MapiReviewFinalStep
            testID="mapi-step-reviewFinal"
            onEditStep={handleEditStep}
            onContinue={handleUpdateTransaction}
          />
        );
      case "headToCashier":
        return (
          <MapiHeadToCashierStep
            testID="mapi-step-headToCashier"
            confirmationCode={mapiConfirmationCode}
            onFinish={handleFinish}
          />
        );
      default:
        return null;
    }
  };

  const handleReviewContinue = async () => {
    const storeState = useSessionStore.getState() as any;
    const agent = storeState.agentId;
    const destCountry = storeState.selectedDestinationCountryCode;
    const selectedOption = storeState.pendingSendOption;

    if (!agent || !destCountry || !selectedOption) return;

    const sendAmt = Number(selectedOption?.sendAmount?.amount?.value ?? 0);
    const sendCurr = selectedOption?.sendAmount?.amount?.currencyCode || "USD";
    const recvCurr =
      selectedOption?.receiveAmount?.amount?.currencyCode || "USD";
    const serviceOption = selectedOption?.serviceOptionCode || "WILL_CALL";
    const serviceOptionRoutingCode = selectedOption?.serviceOptionRoutingCode;

    await executeWithLoading(async () => {
      try {
        const response = await mapiTransactionFieldsSend({
          agentPartnerId: agent,
          destinationCountryCode: destCountry,
          serviceOptionCode: serviceOption,
          ...(serviceOptionRoutingCode && { serviceOptionRoutingCode }),
          amount: sendAmt,
          sendCurrencyCode: sendCurr,
          receiveCurrencyCode: recvCurr,
        });

        if (response.success && response.data) {
          setMapiTransactionFieldsData?.(response.data);
          const fieldInfo = response.data?.fieldInfo ?? [];
          const needSecondaryId = fieldInfo.some(
            (f: any) =>
              f.field === "sender.secondaryIdentification.id" &&
              f.required === true,
          );
          if (needSecondaryId) {
            setSteps((prev) => {
              if (prev.some((s) => s.key === "senderID2")) return prev;
              const senderIDIdx = prev.findIndex((s) => s.key === "senderID");
              const receiverIdx = prev.findIndex(
                (s) => s.key === "receiverDetails",
              );
              if (senderIDIdx === -1 || receiverIdx === -1) return prev;
              const before = prev.slice(0, senderIDIdx + 1);
              const after = prev.slice(senderIDIdx + 1);
              return [...before, { key: "senderID2" }, ...after];
            });
          }
          capturePosthogEvent("Mapi Send Flow - Review Continue", {
            "data-mg-destination-country": destCountry,
            "data-mg-service-option": serviceOption,
          });
          const senderNameIdx = steps.findIndex((s) => s.key === "senderName");
          if (senderNameIdx !== -1) setCurrentStep(senderNameIdx);
        } else {
          showError(response.message || "Failed to load transaction fields.");
        }
      } catch (e: any) {
        const errMessage =
          e?.error?.message ||
          e?.message ||
          "An error occurred loading transaction fields.";
        showError(errMessage);
      }
    }, "Loading fields...");
  };

  const handleContinue = () => {
    if (currentKey === "phone") {
      handleNext();
    } else if (currentKey === "amount") {
      handleAmountContinue();
    } else if (currentKey === "howTheyReceive") {
      handleNext();
    } else if (currentKey === "review") {
      handleReviewContinue();
    } else if (
      currentKey === "senderName" ||
      currentKey === "senderAddress" ||
      currentKey === "senderDOB" ||
      currentKey === "senderID" ||
      currentKey === "senderID2" ||
      currentKey === "receiverDetails" ||
      currentKey === "fraudPrevention"
    ) {
      handleNext();
    }
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === "web" ? undefined : "padding"}
      keyboardVerticalOffset={Platform.OS === "ios" ? 60 : 0}
    >
      <View style={styles.mainWrapper}>
        {currentKey !== "headToCashier" && (
          <View style={styles.header}>
            <View style={styles.topBar}>
              {currentKey !== "phone" ? (
                <TouchableOpacity
                  style={styles.topBarButton}
                  onPress={handleBack}
                  testID="mapiBackButton"
                >
                  <BackIcon width={24} height={24} />
                </TouchableOpacity>
              ) : (
                <Text />
              )}
              <View style={styles.topBarRight}>
                {currentKey === "amount" && (
                  <TouchableOpacity
                    style={styles.topBarCountry}
                    onPress={handleCountryEdit}
                    testID="mapiCountryButton"
                  >
                    {Platform.OS === "web" ? (
                      <View style={styles.flagOverflow}>
                        {alpha2 ? (
                          <ReactCountryFlag
                            countryCode={alpha2}
                            svg
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: 50,
                            }}
                          />
                        ) : (
                          <Text style={{ fontSize: 16 }}>🌍</Text>
                        )}
                      </View>
                    ) : (
                      <Text style={{ fontSize: 16 }}>🌍</Text>
                    )}
                    <Text style={styles.selectedCountryText}>
                      {selectedDestinationCountryName}
                    </Text>
                    <DownArrow width={16} height={16} />
                  </TouchableOpacity>
                )}
                <TouchableOpacity
                  style={[styles.topBarButton, { marginLeft: 16 }]}
                  onPress={handleFinish}
                  testID="mapiCloseButton"
                >
                  <CloseIcon width={24} height={24} />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.progressRow}>
              {steps.map((_, i) => (
                <View
                  key={i}
                  style={[
                    styles.progressSegment,
                    {
                      borderTopLeftRadius: i === 0 ? 9 : 0,
                      borderBottomLeftRadius: i === 0 ? 9 : 0,
                    },
                    {
                      borderTopRightRadius: i === steps.length - 1 ? 9 : 0,
                      borderBottomRightRadius: i === steps.length - 1 ? 9 : 0,
                    },
                    {
                      backgroundColor:
                        i <= currentStep
                          ? Colors.titleTextColor
                          : Colors.searchBackground,
                    },
                  ]}
                />
              ))}
            </View>
          </View>
        )}

        <ScrollView
          ref={scrollRef}
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContainer,
            showGradientFooter && {
              paddingBottom: isGradientExpand ? 340 : 220,
            },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
        >
          {renderCurrentStep()}
        </ScrollView>

        {showContinueButton && (
          <View style={styles.footerContainer}>
            <TouchableOpacity
              testID="mapiContinueButton"
              style={[styles.button, { backgroundColor: "#000" }]}
              onPress={handleContinue}
            >
              <Text style={styles.buttonText}>{t("STRKEY_CONTINUE")}</Text>
            </TouchableOpacity>
          </View>
        )}
        {showGradientFooter &&
          (() => {
            const feeData = getMapiGradientFeeData();
            return (
              <View
                style={styles.footerShrink}
                onLayout={(e: LayoutChangeEvent) =>
                  setFooterHeight(e.nativeEvent.layout.height)
                }
              >
                <GradientBackground style={styles.gradientFooter}>
                  <View style={styles.gradientContent}>
                    <TouchableOpacity
                      style={{ padding: 24 }}
                      onPress={() => setIsGradientExpand(!isGradientExpand)}
                    >
                      <View style={styles.viewTap} />
                    </TouchableOpacity>
                    {isGradientExpand && (
                      <View style={{ flex: 1 }}>
                        <View style={styles.gradientRow}>
                          <Text style={styles.gradientFeeLabel}>
                            {t("REVIEW_EXCHANGE_RATE")}
                          </Text>
                          <Text style={styles.gradientFeeLabel}>
                            1 {feeData.sendCurrency} ={" "}
                            {feeData.exchangeRate.toFixed(4)}{" "}
                            {feeData.receiveCurrency}
                          </Text>
                        </View>
                        <View style={styles.gradientRow}>
                          <Text style={styles.gradientFeeLabel}>
                            {t("REVIEW_FEES")}
                          </Text>
                          <Text style={styles.gradientFeeLabel}>
                            {feeData.fees.toFixed(2)} {feeData.sendCurrency}
                          </Text>
                        </View>
                        <View style={styles.gradientRow}>
                          <Text style={styles.gradientFeeLabel}>
                            {t("US_FED_REMITTANCE_TAX")}
                          </Text>
                          <Text style={styles.gradientFeeLabel}>
                            {feeData.totalSendTax.toFixed(2)}{" "}
                            {feeData.sendCurrency}
                          </Text>
                        </View>
                      </View>
                    )}
                    <View style={[styles.gradientRow, { marginBottom: 16 }]}>
                      <View
                        style={{ flexDirection: "row", alignItems: "center" }}
                      >
                        <WarninBlackIcon width={16} height={16} />
                        <Text style={[styles.gradientPrice, { marginLeft: 8 }]}>
                          {t("REVIEW_TOTAL")}
                        </Text>
                      </View>
                      <Text style={styles.gradientPrice}>
                        {feeData.total.toFixed(2)} {feeData.sendCurrency}
                      </Text>
                    </View>
                    <TouchableOpacity
                      testID="mapiGradientContinueButton"
                      onPress={
                        currentKey === "reviewFinal"
                          ? handleUpdateTransaction
                          : handleReviewContinue
                      }
                      activeOpacity={0.8}
                      style={styles.gradientContinueButton}
                    >
                      <Text style={styles.gradientContinueButtonText}>
                        {currentKey === "reviewFinal"
                          ? t("STRKEY_SUBMIT")
                          : t("STRKEY_CONTINUE")}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </GradientBackground>
              </View>
            );
          })()}
      </View>
    </KeyboardAvoidingView>
  );
};

export default MapiMainSendCashScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    backgroundColor: Colors.white,
    ...(Platform.OS === "web" && {
      height: "calc(100vh - 100px)" as any,
      overflow: "hidden" as any,
    }),
  },
  mainWrapper: { flex: 1, flexDirection: "column" },
  header: { paddingHorizontal: 24 },
  topBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 16,
  },
  topBarButton: { padding: 1 },
  topBarRight: { flexDirection: "row" },
  topBarCountry: {
    flexDirection: "row",
    padding: 8,
    backgroundColor: Colors.textInputBackground,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 39,
    borderRadius: 8,
  },
  selectedCountryText: {
    fontFamily: "Rubik",
    fontSize: 12,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 16,
    marginHorizontal: 8,
  },
  flagOverflow: {
    width: 16,
    height: 16,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
  progressRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
    height: 4,
  },
  progressSegment: { flex: 1, height: 4 },
  scrollView: {
    flex: 1,
    ...(Platform.OS === "web" && {
      height: "calc(100vh - 80px)" as any,
      overflow: "auto" as any,
    }),
  },
  scrollContainer: { flexGrow: 1, paddingBottom: 120 },
  footerContainer: {
    backgroundColor: Colors.white,
    borderTopWidth: 1,
    borderColor: "#F2F2F2",
    paddingHorizontal: 24,
    paddingBottom: 24,
    paddingTop: 14,
  },
  button: {
    backgroundColor: Colors.black,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: "center",
    marginTop: 14,
    marginBottom: Platform.OS === "ios" ? 24 : 16,
  },
  buttonText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "600",
    color: Colors.white,
    lineHeight: 20,
  },
  footerShrink: {
    flexShrink: 0,
  },
  gradientFooter: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    ...(Platform.OS === "web" && {
      position: "fixed" as any,
      bottom: 0,
      left: 0,
      right: 0,
      width: "100%" as any,
      margin: 0 as any,
      boxSizing: "border-box" as any,
      paddingBottom: "env(safe-area-inset-bottom, 0px)" as any,
    }),
  },
  viewTap: {
    height: 4,
    width: 77,
    backgroundColor: Colors.titleTextColor,
    borderRadius: 24,
    alignSelf: "center" as const,
  },
  gradientContent: {
    paddingHorizontal: 24,
  },
  gradientRow: {
    flexDirection: "row" as const,
    justifyContent: "space-between" as const,
    width: "100%" as any,
    marginBottom: 8,
  },
  gradientFeeLabel: {
    fontSize: 14,
    fontFamily: "Rubik",
    fontWeight: "400" as const,
    lineHeight: 22,
    color: Colors.titleTextColor,
  },
  gradientPrice: {
    fontSize: 16,
    fontFamily: "Rubik",
    fontWeight: "500" as const,
    lineHeight: 24,
    color: Colors.titleTextColor,
  },
  gradientContinueButton: {
    width: "100%" as any,
    backgroundColor: Colors.titleTextColor,
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: "center" as const,
    shadowColor: "#000",
    marginBottom: 32,
  },
  gradientContinueButtonText: {
    fontFamily: "Rubik",
    fontSize: 16,
    fontWeight: "500" as const,
    color: Colors.white,
  },
});
